import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  FunnelIcon,
} from '@heroicons/react/20/solid';
import DashboardLayout from '../dashboard-layout/DashboardLayout';
import SectionHeader from '../../elements/SectionHeader';
import Pagination from '../../elements/pagination/Pagination';
import { useDocumentTitle } from '../../helpers/hooks';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { getCanManageEnrichedLeads } from '../../app/permissionsSchema';
import { RootState } from '../../app/store';
import { useGetEnrichedLeadsQuery } from './enrichedLeadsAPI';
import {
  setPageNumber,
  setSearchInput,
  setLinkStatus,
  resetEnrichedLeadsFilters,
  initialState,
} from './enrichedLeadsSlice';
import EnrichedLeadCompanyCard from './EnrichedLeadCompanyCard';

export default function EnrichedLeadsPage() {
  useDocumentTitle('Enriched Leads Management');

  const canManageEnrichedLeads = useAppSelector(getCanManageEnrichedLeads);
  const history = useHistory();
  const dispatch = useAppDispatch();

  const {
    pageNumber,
    pageSize,
    searchInput,
    linkStatus,
  } = useSelector((state: RootState) => state.enrichedLeads);

  const [localSearch, setLocalSearch] = useState(searchInput);

  useEffect(() => {
    if (!canManageEnrichedLeads) history.push('/page404');
  }, [canManageEnrichedLeads, history]);

  const { data, isLoading, isFetching } = useGetEnrichedLeadsQuery({
    pageNumber,
    pageSize,
    searchInput: searchInput || undefined,
    linkStatus: linkStatus || undefined,
  });

  const handleSearchSubmit = () => {
    dispatch(setSearchInput(localSearch));
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearchSubmit();
  };

  const handleClearSearch = () => {
    setLocalSearch('');
    dispatch(setSearchInput(''));
  };

  const isFiltered = JSON.stringify({
    searchInput,
    linkStatus,
  }) !== JSON.stringify({
    searchInput: initialState.searchInput,
    linkStatus: initialState.linkStatus,
  });

  const companies = data?.data || [];

  return (
    <DashboardLayout>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
        <SectionHeader title="Enriched Leads Management" />

        <div className="bg-white p-6 shadow-sm rounded-md mt-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <input
                type="text"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search by company name..."
                className="w-full border border-neutral-300 rounded-full px-4 py-2.5 pr-20 text-sm font-semibold text-neutral-900 placeholder-neutral-500 focus:ring-1 focus:ring-main-600 focus:border-main-600 outline-none bg-neutral-50 focus:bg-white transition-colors"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-1">
                {localSearch && (
                  <button type="button" onClick={handleClearSearch} className="p-1">
                    <XMarkIcon className="h-4 w-4 text-neutral-400 hover:text-neutral-600" />
                  </button>
                )}
                <button type="button" onClick={handleSearchSubmit} className="p-1">
                  <MagnifyingGlassIcon className="h-4 w-4 text-main-600" />
                </button>
              </div>
            </div>

            <select
              value={linkStatus}
              onChange={(e) => dispatch(setLinkStatus(e.target.value))}
              className="border border-neutral-300 rounded-full px-4 py-2.5 text-sm font-semibold text-neutral-700 bg-neutral-50 focus:ring-1 focus:ring-main-600 focus:border-main-600 outline-none cursor-pointer"
            >
              <option value="">All Companies</option>
              <option value="linked">Linked</option>
              <option value="notLinked">Not Linked</option>
            </select>

            {isFiltered && (
              <button
                type="button"
                onClick={() => {
                  dispatch(resetEnrichedLeadsFilters());
                  setLocalSearch('');
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold border border-neutral-300 text-neutral-600 bg-white hover:bg-neutral-25 transition-colors"
              >
                <FunnelIcon className="h-3.5 w-3.5" />
                Clear
              </button>
            )}
          </div>

          {data && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-xs text-neutral-500 font-semibold">
                {`${data.totalRecords} compan${data.totalRecords !== 1 ? 'ies' : 'y'} found`}
              </p>
              {isFetching && !isLoading && (
                <ArrowPathIcon className="h-3.5 w-3.5 text-neutral-400 animate-spin" />
              )}
            </div>
          )}
        </div>

        {isLoading && (
          <div className="mt-12 flex w-full justify-center items-center animate-pulse">
            <ArrowPathIcon className="h-4 w-4 text-neutral-600 mr-2.5 animate-spin" />
            <span className="font-semibold text-neutral-600">Loading leads...</span>
          </div>
        )}

        {!isLoading && companies.map((company) => (
          <EnrichedLeadCompanyCard key={company.id} company={company} />
        ))}

        {!isLoading && companies.length === 0 && data && (
          <div className="bg-white shadow-sm rounded-md p-12 mt-3 text-center">
            <p className="text-neutral-500 font-semibold">No companies found matching your filters.</p>
          </div>
        )}

        {data && data.totalPages > 1 && (
          <div className="bg-white shadow-sm rounded-md p-4 mt-3">
            <Pagination
              currentPage={data.pageNumber}
              pageSize={data.pageSize}
              totalRecords={data.totalRecords}
              totalPages={data.totalPages}
              onPageChange={(page) => dispatch(setPageNumber(page))}
              label="companies"
            />
          </div>
        )}
      </main>
    </DashboardLayout>
  );
}
